import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowDownLeft, ArrowUp } from 'lucide-react';
import { useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { Barra } from '@/componentes/Esqueleto';
import { formatarCompetencia, formatarReais } from '@/lib/formato';
import { ehSemPermissao, fraseDoSemPermissao } from '@/lib/perfil-da-licenca';
import { explicarFalha } from '@/lib/pedido';
import { useSessao } from '@/modulos/sessao/consultas';

import { apiDaRenovacao } from './api';
import { JanelaDoEstudoNovo } from './JanelaDoEstudoNovo';
import { codigoDoEstudo, horas, janelaEscrita, percentual, reaisPorHora, textoDaTendencia } from './renovacao';
import { SeloDoEstudo } from './Selo';

/**
 * A SEÇÃO "ECONOMIA DA MÁQUINA" NA FICHA (módulo 20, ferramenta 2): os 12
 * meses fechados da máquina — custo total por hora, a tendência do custo de
 * manutenção (só com 6 meses com horas), a margem por hora, a
 * disponibilidade, o valor contábil e a vida consumida, o MTBF e o MTTR do
 * 18 —, os últimos meses e os estudos dela. Fica ao lado do "Custo da
 * máquina" (o mês fechado), e só para quem lê a renovação.
 */
export function EconomiaDaMaquina({ equipamentoId }: { readonly equipamentoId: string }) {
  const sessao = useSessao();
  const permissoes = sessao.data?.permissoes ?? [];
  const pode = permissoes.includes('renovacao.ler');
  const grava = permissoes.includes('estudo.gravar');
  const [abrindo, setAbrindo] = useState(false);
  const consulta = useQuery({ queryKey: ['renovacao', 'historico', equipamentoId], queryFn: () => apiDaRenovacao.historico(equipamentoId), enabled: pode, meta: { oQueLia: 'a economia da máquina' } });
  if (!pode) return null;
  const h = consulta.data;
  if (consulta.isError && !h) {
    return (
      <section className="cartao flex flex-col gap-2 p-4" aria-label="Economia da máquina">
        <h3 className="txt-subtitulo text-tinta">Economia da máquina</h3>
        <p className="txt-apoio text-tinta-secundaria">{ehSemPermissao(consulta.error) ? fraseDoSemPermissao(consulta.error, 'a economia da máquina') : `${explicarFalha(consulta.error).erro}. ${explicarFalha(consulta.error).comoResolver}`}</p>
      </section>
    );
  }
  if (!h) {
    return (
      <section aria-hidden="true" className="cartao flex flex-col gap-3 p-4">
        <h3 className="txt-subtitulo text-tinta">Economia da máquina</h3>
        <Barra className="h-6 w-3/4" />
        <Barra className="h-3 w-full" />
      </section>
    );
  }
  const e = h.economia;
  const rascunho = h.estudos.find((x) => x.situacao === 'rascunho') ?? null;
  const itens: readonly (readonly [string, string])[] = [
    ['Custo total por hora', e.horasInsuficientes ? `horas insuficientes (${horas(e.horasProdutivas)})` : reaisPorHora(e.custoTotalPorHora)],
    ['Margem por hora', h.comReceita ? reaisPorHora(e.margemPorHora) : 'sem a licença da receita'],
    ['Disponibilidade', percentual(e.disponibilidade)],
    ['Valor contábil gerencial', h.valorContabil.valor === null ? 'sem depreciação cadastrada' : `${formatarReais(h.valorContabil.valor)} · ${percentual(h.valorContabil.vidaConsumida)} da vida`],
    ['MTBF · MTTR', `${h.confiabilidade.mtbf.valor === null ? 'sem base' : horas(h.confiabilidade.mtbf.valor)} · ${h.confiabilidade.mttr.valor === null ? 'sem base' : horas(h.confiabilidade.mttr.valor)}`],
    ['Corretivas classificadas na janela', String(h.confiabilidade.falhasNaJanela)],
  ];
  return (
    <section className="cartao flex flex-col gap-4 p-4" aria-label="Economia da máquina">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="txt-subtitulo text-tinta">Economia da máquina</h3>
        <span className="txt-apoio text-tinta-secundaria">{janelaEscrita(e.de, e.ate)}</span>
      </div>
      <div className="flex items-center gap-2">
        {h.tendencia.sentido === 'sobe' ? <ArrowUp aria-hidden="true" className="size-4 shrink-0 text-parada" /> : null}
        {h.tendencia.sentido === 'desce' ? <ArrowDownLeft aria-hidden="true" className="size-4 shrink-0 text-operando" /> : null}
        <p className="txt-corpo text-tinta">{`Manutenção por hora: ${textoDaTendencia(h.tendencia)}`}</p>
      </div>
      <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-2">
        {itens.map(([r, v]) => (
          <div key={r} className="flex min-w-0 flex-col">
            <dt className="txt-apoio text-tinta-secundaria">{r}</dt>
            <dd className="txt-corpo tabular-nums text-tinta">{v}</dd>
          </div>
        ))}
      </dl>
      <ul className="flex flex-col divide-y divide-borda-sutil" aria-label="Os últimos meses">
        {[...h.serie].reverse().slice(0, 4).map((m) => (
          <li key={m.competencia} className="txt-apoio flex justify-between gap-3 py-1.5">
            <span className="text-tinta-secundaria">{`${formatarCompetencia(m.competencia)}${m.emAndamento ? ' · mês aberto' : ''}`}</span>
            <span className="numero text-tinta">{`${horas(m.horasProdutivas)} · ${reaisPorHora(m.custoTotalPorHora)}`}</span>
          </li>
        ))}
      </ul>
      <p className="txt-legenda text-pretty text-tinta-apagada">{h.frases.valor}</p>
      <div className="flex flex-wrap items-center gap-2">
        {h.estudos.filter((x) => x.situacao !== 'arquivado').slice(0, 3).map((x) => (
          <Link key={x.id} to="/equipamentos/renovacao/estudos/$estudoId" params={{ estudoId: x.id }} className="flex items-center gap-2 rounded-controle px-2 py-1 hover:bg-rebaixada">
            <span className="codigo text-tinta">{codigoDoEstudo(x.numero)}</span>
            <SeloDoEstudo situacao={x.situacao} />
          </Link>
        ))}
        {grava && !rascunho ? <Botao nivel="secundario" tamanho="p" onClick={() => { setAbrindo(true); }}>Abrir estudo</Botao> : null}
      </div>
      {grava ? <JanelaDoEstudoNovo aberta={abrindo} aoFechar={() => { setAbrindo(false); }} equipamentoId={equipamentoId} /> : null}
    </section>
  );
}
